/**
 * SSR search engine definition — Coveo @coveo/headless-react SSR (Headless 3).
 *
 * This module defines the engine + controllers statically with a placeholder
 * token. The server (SearchSsrPage) sets a shared ANONYMOUS token via
 * `setAccessToken` and runs `fetchStaticState` (true SSR); the client provider
 * (SearchEngineProvider) re-sets that same token + navigator context and calls
 * `hydrateStaticState`. All search content is public, so there is no per-user
 * token — pipeline routing rides in per-request `context` values instead.
 *
 * IMPORT RULE: import the SSR/define API ONLY from `@coveo/headless-react/ssr`.
 * A bare `@coveo/headless` resolves to atomic-react's v2.39 (no `/ssr`).
 */
import {
  buildDateRange,
  buildNumericRange,
  defineBreadcrumbManager,
  defineContext,
  defineDateFacet,
  defineDictionaryFieldContext,
  defineDidYouMean,
  defineFacet,
  defineNumericFacet,
  definePager,
  defineQuerySummary,
  defineRecentQueriesList,
  defineResultList,
  defineResultsPerPage,
  defineSearchBox,
  defineSearchEngine,
  defineSearchParameterManager,
  defineSearchStatus,
  defineSort,
} from '@coveo/headless-react/ssr';
import { DEFAULT_SEARCH_PAGE_SIZE } from '@lib/constants/coveo';
import { createAnalyticsClientMiddleware } from '@lib/coveo/analyticsClientMiddleware';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { fetchSearchToken } from '@lib/coveo/searchTokenHelpers';
import * as Sentry from '@sentry/nextjs';
import { DEFAULT_LOCALE } from 'utils';
import {
  DATE_RANGE_KEYS,
  PRICE_RANGES,
  RATING_RANGES,
} from '../../utils/facetRanges';
import { searchTelemetryMiddleware } from './searchTelemetry';

// Result fields Coveo must return (otherwise only title/uri come back). Lives
// with the controller definition rather than the ResultList component.
const RESULT_FIELDS_TO_INCLUDE = [
  'ec_sale',
  'ec_name',
  'ec_thumbnails',
  'ec_price',
  'ec_price_filter',
  'ec_rating',
  'ec_rating_count',
  'ec_rating_sort',
  'ec_category',
  'publisher_name',
  'publisher_id',
  'ec_product_id',
  'ec_tags',
  'ec_is_new_release',
  'min_unity_version',
  'ec_flash_teaser_starts_at',
  'ec_flash_promo_starts_at',
];

// Facet range definitions (start/end + display metadata) live in ../facetRanges,
// the single source shared with the display layer. This module only reads
// `days` to build the Coveo facets.
//
// Release-date ranges use Coveo *relative* dates (`past N days` .. `now`) rather
// than absolute timestamps. buildControllerDefinitions() runs once at module
// load, so timestamps derived from `new Date()` here would freeze at server-boot
// time — on a long-lived server "Last 24 hours" would really mean "the 24h ending
// when the process started". Relative ranges are resolved by Coveo per query, so
// they stay correct, and they serialize to the same `past-N-day..now` form the
// legacy /listing middleware already emits.
function dateRangeValues() {
  return DATE_RANGE_KEYS.map(({ days }) =>
    buildDateRange({
      start: { period: 'past', amount: days, unit: 'day' },
      end: { period: 'now' },
      endInclusive: true,
    })
  );
}

/**
 * Build the engine's controller-definition map. Every controller the full SSR
 * search experience needs is defined up front — even those without UI yet — so
 * later slices only add components and never touch the engine definition.
 *
 * `defaultNumberOfResults` is a definition-time option (defineResultsPerPage
 * takes no per-build props), which is why it is threaded through the factory:
 * the server page resolves the flag-driven page size per request and the
 * client provider re-creates its definition with the same value, so hydration
 * replays the server search instead of re-fetching at a different size.
 */
function buildControllerDefinitions(defaultNumberOfResults: number) {
  return {
    searchBox: defineSearchBox({ options: { numberOfSuggestions: 5 } }),
    recentQueries: defineRecentQueriesList({ options: { maxLength: 5 } }),
    resultList: defineResultList({
      options: { fieldsToInclude: RESULT_FIELDS_TO_INCLUDE },
    }),
    pager: definePager(),
    sort: defineSort(),
    querySummary: defineQuerySummary(),
    breadcrumbManager: defineBreadcrumbManager(),
    resultsPerPage: defineResultsPerPage({
      initialState: { numberOfResults: defaultNumberOfResults },
    }),
    didYouMean: defineDidYouMean(),
    searchStatus: defineSearchStatus(),
    context: defineContext(),
    dictionaryFieldContext: defineDictionaryFieldContext(),

    priceFacet: defineNumericFacet({
      options: {
        field: 'ec_price_filter',
        generateAutomaticRanges: false,
        currentValues: PRICE_RANGES.map((r) =>
          buildNumericRange({
            start: r.start,
            end: r.end,
            endInclusive: r.start === r.end || r.end === 999999,
          })
        ),
      },
    }),
    ratingFacet: defineNumericFacet({
      options: {
        field: 'ec_rating',
        generateAutomaticRanges: false,
        currentValues: RATING_RANGES.map((r) =>
          buildNumericRange({ start: r.start, end: r.end, endInclusive: true })
        ),
      },
    }),
    publisherFacet: defineFacet({
      options: {
        field: 'publisher_name',
        // A selection restored from the URL must appear in the value list.
        numberOfValues: 1000,
        sortCriteria: 'occurrences',
      },
    }),
    categoryFacet: defineFacet({
      options: {
        field: 'ec_category',
        numberOfValues: 150,
        sortCriteria: 'alphanumeric',
      },
    }),
    saleFacet: defineFacet({
      options: { field: 'ec_sale_filters', numberOfValues: 10 },
    }),
    releaseDateFacet: defineDateFacet({
      options: {
        field: 'first_published_at',
        generateAutomaticRanges: false,
        currentValues: dateRangeValues(),
      },
    }),

    // MUST be defined last: the search parameter manager requires the other
    // controllers (especially the facets) to be initialized first so it can
    // restore their selections from the URL. Controllers are initialized in
    // definition order, so declaring this earlier silently breaks facet URL
    // restore (query/sort still work, but `f-*` facet params are dropped).
    searchParameterManager: defineSearchParameterManager(),
  };
}

/**
 * Factory for a per-surface engine definition, parameterized by searchHub
 * (search / listing — production keeps a distinct pipeline and API key per
 * hub) and the request locale (`search.locale` drives
 * Coveo's linguistic processing — stemming, lemmatization; the `language`
 * context value alone does not set it).
 * `analyticsMode: 'legacy'` keeps analytics on the current UA protocol.
 * The access token is a placeholder here; the server page and client provider
 * replace it via `setAccessToken` before fetching state.
 * `getPageSize` lets the analytics middleware read the hydrated engine's results
 * per page: the client provider passes one, while the server page and the
 * singletons below omit it because their engines never emit analytics.
 */
export function createSearchEngineDefinition(
  hub: CoveoSearchHub,
  locale: string = DEFAULT_LOCALE,
  defaultNumberOfResults: number = DEFAULT_SEARCH_PAGE_SIZE,
  getPageSize?: () => number | undefined
) {
  return defineSearchEngine({
    configuration: {
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID!,
      accessToken: 'pending-token-set-client-side',
      // Renew on 401 by fetching a fresh ANONYMOUS token (no cache). Must
      // return a string: a rejection would propagate into the SDK's token
      // manager and leave a broken token endpoint invisible in monitoring, so
      // report and resolve to '' like the Headless 2 renew path
      // (getNewSearchToken).
      renewAccessToken: () =>
        fetchSearchToken(hub, true).then(
          (r) => r?.token ?? '',
          (error) => {
            Sentry.withScope((scope) => {
              scope.setTags({
                'coveo.hub': hub,
                'coveo.step': 'token_renew',
              });
              Sentry.captureException(error);
            });
            return '';
          }
        ),
      search: { searchHub: hub, locale },
      // Emit analytics on the legacy UA protocol (the Headless 3 default is the
      // new event protocol). Built disabled: tracking is gated on OneTrust
      // consent, and the consent state is only known client-side, so
      // AnalyticsConsentSync enables tracking on the hydrated engine once
      // functional consent is granted. The server's fetchStaticState engine
      // never gets that signal and stays disabled. The shared
      // analyticsClientMiddleware mirrors UA events into the GTM dataLayer.
      analytics: {
        analyticsMode: 'legacy',
        enabled: false,
        analyticsClientMiddleware: createAnalyticsClientMiddleware(getPageSize),
      },
    },
    middlewares: [searchTelemetryMiddleware],
    controllers: buildControllerDefinitions(defaultNumberOfResults),
  });
}

/**
 * The SEARCH surface's client-side singleton: the generated controller hooks
 * and StateProvider must come from one shared definition, but nothing builds
 * engines on it. The server page (SearchSsrPage) and the client provider
 * (SearchEngineProvider) both create per-use definitions via the factory
 * above, because setNavigatorContextProvider/setAccessToken store per-request
 * state on the definition and build()/hydrateStaticState register an
 * engine-retaining token-manager callback that is never released — engines
 * built on this singleton would be retained for the life of the process/SPA
 * session.
 *
 * `defineSearchEngine` creates one React context per call, so these hooks only
 * read state inside THIS definition's StateProvider. The listing surface has
 * its own singleton (listingEngineDefinition) and the shared ssr/ components
 * reach whichever one their page mounted through SearchDefinitionContext —
 * hence no `controllers` re-export here.
 *
 * Module scope means no request locale here, so this stays on DEFAULT_LOCALE —
 * harmless, because no engine is ever built on it: the per-use definitions
 * created by SearchSsrPage and SearchEngineProvider both carry the route
 * locale, so server and client searches run with it.
 */
export const searchEngineDefinition = createSearchEngineDefinition(
  CoveoSearchHub.SEARCH
);
